import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getLangFromWindow } from "@/utils/seoLang";

const content = {
  ru: {
    title: "Политика конфиденциальности",
    updated: "Дата последнего обновления: 28 марта 2025 г.",
    sections: [
      {
        title: "1. Введение",
        text: `Настоящая Политика конфиденциальности описывает, как YUCCA Flowers (далее — «мы», «Сервис») собирает, использует и защищает информацию пользователей сайта yuccaflower.com.

Мы уважаем вашу конфиденциальность и стремимся защитить ваши персональные данные.`
      },
      {
        title: "2. Какие данные мы собираем",
        text: `В рамках использования сайта мы можем собирать следующую информацию:

Данные, предоставленные вами при заказе:
— Имя и контактные данные получателя букета
— Адрес доставки
— Номер телефона для связи через WhatsApp/Telegram

Автоматически собираемые данные:
— IP-адрес и приблизительное местоположение
— Тип браузера и устройства
— Страницы, которые вы посещаете на сайте
— Данные аналитики (Google Analytics) для улучшения работы сайта`
      },
      {
        title: "3. Как мы используем данные",
        text: `Собранные данные используются для:
— Обработки и выполнения вашего заказа
— Связи с вами по вопросам заказа
— Передачи информации партнёру-исполнителю для доставки букета
— Улучшения работы сайта и качества обслуживания
— Аналитики посещаемости и эффективности рекламных кампаний`
      },
      {
        title: "4. Передача данных третьим лицам",
        text: `YUCCA Flowers является агрегатором, и для выполнения заказа мы передаём необходимую информацию (адрес доставки, контактные данные получателя) партнёрам-исполнителям — независимым флористам и службам доставки.

Мы также используем следующие сторонние сервисы:
— Google Analytics — для анализа посещаемости сайта
— Google Ads — для показа рекламы и отслеживания конверсий

Мы не продаём, не сдаём в аренду и не передаём ваши персональные данные третьим лицам в маркетинговых целях.`
      },
      {
        title: "5. Cookies и технологии отслеживания",
        text: `Сайт использует файлы cookie и аналогичные технологии для:
— Корректной работы сайта (выбор языка, валюты)
— Сбора анонимной статистики посещаемости (Google Analytics)
— Отслеживания эффективности рекламных кампаний (Google Ads)

Вы можете отключить cookies в настройках вашего браузера, однако это может повлиять на функциональность сайта.`
      },
      {
        title: "6. Безопасность данных",
        text: `Мы принимаем разумные меры для защиты ваших персональных данных от несанкционированного доступа, изменения, раскрытия или уничтожения. Сайт использует защищённое соединение (HTTPS).

Однако ни один метод передачи данных через интернет не является полностью безопасным, и мы не можем гарантировать абсолютную безопасность.`
      },
      {
        title: "7. Хранение данных",
        text: `Мы храним ваши персональные данные только столько, сколько это необходимо для целей, описанных в данной Политике. Данные заказов хранятся для обеспечения качества обслуживания и разрешения возможных споров.`
      },
      {
        title: "8. Ваши права",
        text: `Вы имеете право:
— Запросить информацию о ваших персональных данных, которые мы храним
— Потребовать исправления неточных данных
— Потребовать удаления ваших персональных данных
— Отозвать согласие на обработку данных

Для реализации этих прав свяжитесь с нами через WhatsApp или Telegram.`
      },
      {
        title: "9. Изменения в политике",
        text: `Мы оставляем за собой право обновлять данную Политику конфиденциальности. Актуальная версия всегда доступна на данной странице. Мы рекомендуем периодически проверять эту страницу.`
      },
      {
        title: "10. Контакты",
        text: `По вопросам конфиденциальности и защиты данных:
— WhatsApp: +90 531 622 43 78
— Telegram: @Yucca_flower
— Сайт: yuccaflower.com`
      }
    ]
  },
  en: {
    title: "Privacy Policy",
    updated: "Last updated: March 28, 2025",
    sections: [
      {
        title: "1. Introduction",
        text: `This Privacy Policy describes how YUCCA Flowers (hereinafter referred to as "we", "Service") collects, uses, and protects information of users of the website yuccaflower.com.

We respect your privacy and strive to protect your personal data.`
      },
      {
        title: "2. Data We Collect",
        text: `When using the website, we may collect the following information:

Data you provide when ordering:
— Name and contact details of the bouquet recipient
— Delivery address
— Phone number for communication via WhatsApp/Telegram

Automatically collected data:
— IP address and approximate location
— Browser and device type
— Pages you visit on the website
— Analytics data (Google Analytics) to improve website performance`
      },
      {
        title: "3. How We Use Data",
        text: `Collected data is used for:
— Processing and fulfilling your order
— Contacting you regarding your order
— Transferring information to the partner executor for bouquet delivery
— Improving website performance and service quality
— Traffic analytics and advertising campaign effectiveness`
      },
      {
        title: "4. Data Sharing with Third Parties",
        text: `YUCCA Flowers is an aggregator, and to fulfill orders, we share necessary information (delivery address, recipient contact details) with partner executors — independent florists and delivery services.

We also use the following third-party services:
— Google Analytics — for website traffic analysis
— Google Ads — for advertising and conversion tracking

We do not sell, rent, or share your personal data with third parties for marketing purposes.`
      },
      {
        title: "5. Cookies and Tracking Technologies",
        text: `The website uses cookies and similar technologies for:
— Proper website functionality (language selection, currency)
— Collecting anonymous traffic statistics (Google Analytics)
— Tracking advertising campaign effectiveness (Google Ads)

You can disable cookies in your browser settings, however, this may affect website functionality.`
      },
      {
        title: "6. Data Security",
        text: `We take reasonable measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction. The website uses a secure connection (HTTPS).

However, no method of data transmission over the internet is completely secure, and we cannot guarantee absolute security.`
      },
      {
        title: "7. Data Retention",
        text: `We retain your personal data only for as long as necessary for the purposes described in this Policy. Order data is stored to ensure service quality and resolve potential disputes.`
      },
      {
        title: "8. Your Rights",
        text: `You have the right to:
— Request information about your personal data that we store
— Require correction of inaccurate data
— Require deletion of your personal data
— Withdraw consent for data processing

To exercise these rights, contact us via WhatsApp or Telegram.`
      },
      {
        title: "9. Policy Changes",
        text: `We reserve the right to update this Privacy Policy. The current version is always available on this page. We recommend periodically checking this page.`
      },
      {
        title: "10. Contact Information",
        text: `For privacy and data protection inquiries:
— WhatsApp: +90 531 622 43 78
— Telegram: @Yucca_flower
— Website: yuccaflower.com`
      }
    ]
  }
};

export default function PrivacyPage() {
  const [lang, setLang] = useState(() => getLangFromWindow(["ru", "en"]));
  const t = content[lang];

  useEffect(() => {
    document.title = lang === "ru"
      ? "Политика конфиденциальности — YUCCA Flowers"
      : "Privacy Policy — YUCCA Flowers";
  }, [lang]);

  return (
    <div className="min-h-screen bg-zinc-950 text-gray-200" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 mb-8 text-sm transition-colors" data-testid="privacy-back-link">
          <ArrowLeft className="w-4 h-4" />
          {lang === "ru" ? "На главную" : "Back to Home"}
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="privacy-title">{t.title}</h1>
          <button
            onClick={() => { const newLang = lang === "ru" ? "en" : "ru"; setLang(newLang); localStorage.setItem("yucca_lang", newLang); }}
            className="text-sm px-3 py-1.5 rounded-full border border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
            data-testid="privacy-lang-toggle"
          >
            {lang === "ru" ? "EN" : "RU"}
          </button>
        </div>

        <p className="text-gray-500 text-sm mb-10">{t.updated}</p>

        <div className="space-y-8">
          {t.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-lg font-semibold text-white mb-3">{section.title}</h2>
              <p className="text-gray-300 leading-relaxed whitespace-pre-line text-sm">{section.text}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
